import { isFacingTextBand, FACING_TEXT_BANDS } from '@/lib/pdf/facing-text-layout';
import { MANGA_PRODUCT_TYPE, PICTURE_BOOK_PRODUCT_TYPE, isComicScriptComplete } from '@/types/manga';
import { bookAwaitingPurchase } from '@/lib/orders/book-status-guard';

/** True only for comic (manga-comic) books; accepts DB (snake_case) or API (camelCase) rows. */
export function isComicBook(
  book: { product_type?: string | null; productType?: string | null } | null | undefined
): boolean {
  if (!book) return false;
  return book.product_type === MANGA_PRODUCT_TYPE || book.productType === MANGA_PRODUCT_TYPE;
}

/** Where a customer reads a finished book: the comic reader for comics, the picture-book viewer otherwise. */
export function bookViewPath(
  book: { id: string; product_type?: string | null; productType?: string | null }
): string {
  return isComicBook(book) ? `/comic/${book.id}` : `/book/${book.id}/view`;
}

/** True for a picture book drawn by the comic engine (stamped metadata.engine === 'comic'). */
export function isComicEnginePictureBook(
  book: { product_type?: string | null; productType?: string | null; metadata?: { engine?: unknown } | null } | null | undefined
): boolean {
  if (!book) return false;
  const isPicture = book.product_type === PICTURE_BOOK_PRODUCT_TYPE || book.productType === PICTURE_BOOK_PRODUCT_TYPE;
  return isPicture && book.metadata?.engine === 'comic';
}

export type PictureBookResumeTarget = 'old-preview' | 'flow-preview' | 'flow-story' | 'post-purchase';

/**
 * Where a book opens when it is resumed (`/create-book?book=`) or opened at the old `/book/<id>/preview` page.
 * Accepts DB (snake_case) or API (camelCase) rows.
 * - no comic-engine stamp: `old-preview` (old wizard books, comics: exactly as today);
 * - stamped and not yet bought: the flow, at the story step once the page script is complete, else the preview;
 * - stamped otherwise (bought, in process, failed): `post-purchase`, today's `/book/<id>/preview` page.
 */
export function pictureBookResumeTarget(book: any): PictureBookResumeTarget {
  if (!isComicEnginePictureBook(book)) return 'old-preview';
  if (!bookAwaitingPurchase(book.status)) return 'post-purchase';
  const script = book.metadata?.manga?.script;
  const pageCount =
    Number(book.page_count ?? book.pageCount ?? book.metadata?.manga?.settings?.pageCount) ||
    (Array.isArray(script?.pages) ? script.pages.length : 0);
  return isComicScriptComplete(script, pageCount) ? 'flow-story' : 'flow-preview';
}

// The O/W band rule lives in lib/pdf/facing-text-layout.js (plain CommonJS, also loaded by the PDF worker); re-exported here.
export { isFacingTextBand, FACING_TEXT_BANDS };
